import React from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, CurrencyAdornment, DateTimePicker, NumberBox, SearchBox, SelectOption, TextAreaBox, TextBox } from '@guryou/html-components';
import { printInputDate } from '@guryou/utils/build/dateTimeUtils.datefns';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { createTheme, ThemeProvider, useTheme } from '@material-ui/core/styles';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import styled from 'styled-components';

import { dealTokens } from './designTokens';
import WizardSlider from './WizardSlider';

import '../../modules/providers/containers/workoutPlansTemplates/index.css';

const C = {
  accent: dealTokens.color.primary,
  ink: dealTokens.color.text,
  muted: dealTokens.color.textMuted,
};

const BrandedRadio = styled(Radio)`
  &.Mui-checked {
    color: ${C.accent};
  }
`;

const BrandedCheckbox = styled(Checkbox)`
  && {
    width: 20px;
    height: 20px;
    padding: 0;
    flex-shrink: 0;
  }
`;

const CheckboxMark = styled.span`
  position: relative;
  display: inline-flex;
  width: 20px;
  height: 20px;
  box-sizing: border-box;
  border: 1px solid ${p => (p['data-checked'] ? C.accent : dealTokens.color.borderStrong)};
  border-radius: ${dealTokens.radius.sm}px;
  background: ${p => (p['data-checked'] ? C.accent : dealTokens.color.surface)};
  box-shadow: ${p => (p['data-checked'] ? '0 1px 3px rgba(230, 97, 106, 0.22)' : 'inset 0 0 0 1px rgba(37, 37, 37, 0.02)')};
  transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;

  ${p =>
    p['data-checked'] &&
    `
      &::after {
        content: '';
        position: absolute;
        left: 7px;
        top: 3px;
        width: 5px;
        height: 10px;
        border: solid #FFFFFF;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    `}
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${p => (p['data-single-column'] ? '1fr' : `repeat(auto-fit, minmax(${p.minColumnWidth || 190}px, 1fr))`)};
  align-items: start;
  gap: ${p => p.rowGap || 18}px ${p => p.columnGap || 14}px;
  width: 100%;
  margin-bottom: ${p => (p.noMargin ? 0 : 20)}px;

  & > * {
    min-width: 0;
    width: 100%;
    max-width: none !important;
    margin-bottom: 0;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.section`
  padding: 18px;
  margin-bottom: ${p => (p['data-no-margin'] ? 0 : 16)}px;
  border: 1px solid ${p => (p['data-accent'] ? 'rgba(230, 97, 106, 0.22)' : dealTokens.color.border)};
  border-radius: ${dealTokens.radius.lg}px;
  background: ${p => (p['data-accent'] ? 'rgba(230, 97, 106, 0.055)' : dealTokens.color.surface)};
  &[data-variant='plain'] {
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 0;
    margin-bottom: 0;
  }
  &[data-variant='plain'] + &[data-variant='plain'] {
    border-top: 1px solid ${dealTokens.color.border};
    margin-top: 24px;
    padding-top: 24px;
  }
`;

const withSystemInputClass = InputProps => ({
  ...InputProps,
  className: ['studio-input', InputProps?.className].filter(Boolean).join(' '),
});

export const SystemFieldControl = ({ label, name, error, required = false, children }) => (
  <div style={{ width: '100%', minWidth: 0 }}>
    {label && (
      <label
        htmlFor={name}
        style={{ display: 'block', marginBottom: 6, color: error ? '#E53C4C' : '#5F5E5A', fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}
      >
        {label}
        {required && (
          <span aria-hidden="true" style={{ color: dealTokens.color.danger }}>
            {' '}
            *
          </span>
        )}
      </label>
    )}
    {children}
  </div>
);

// TextBox/SelectOption default to MUI's margin="normal" (extra top/bottom margin meant
// for label-less legacy forms). SystemFieldControl already provides the label and its own
// spacing, so every System* control here defaults margin to "none" — otherwise the two
// stack and the label ends up floating far above its box (this shipped without the
// override in GeneralInformation.js and looked broken; invoicing/index.js, GroupFormModal
// and GymAccessConfig had already been passing margin="none" by hand on every field).
export const SystemTextInput = ({ InputProps, label, name, error, required = false, fullWidth = true, margin = 'none', ...props }) => (
  <SystemFieldControl label={label} name={name} error={error} required={required}>
    <TextBox
      {...props}
      id={props.id || name}
      inputProps={{ ...props.inputProps, 'aria-required': required || undefined }}
      name={name}
      required={required}
      error={error}
      forceError={!!error}
      fullWidth={fullWidth}
      margin={margin}
      InputProps={withSystemInputClass(InputProps)}
    />
  </SystemFieldControl>
);

export const SystemTextArea = ({ InputProps, label, name, error, rows = 4, fullWidth = true, margin = 'none', ...props }) => (
  <SystemFieldControl label={label} name={name} error={error}>
    <TextAreaBox
      {...props}
      name={name}
      rows={String(rows)}
      error={error}
      forceError={!!error}
      fullWidth={fullWidth}
      margin={margin}
      InputProps={withSystemInputClass(InputProps)}
    />
  </SystemFieldControl>
);

// NumberBox/PriceBox render the same MUI notched-outline/floating-label chrome as bare
// TextBox — flag components (BillingInformationForm.js etc.) leave those alone because
// there's no System* counterpart, but a plain numeric/price field IS just TextBox
// underneath (NumberBox/PriceBox are thin TextBox wrappers), so it gets the same
// SystemFieldControl + `studio-input` treatment as SystemTextInput instead of standing
// out as the one field on the page still in the old style.
export const SystemNumberInput = ({ InputProps, label, name, error, fullWidth = true, margin = 'none', ...props }) => (
  <SystemFieldControl label={label} name={name} error={error}>
    <NumberBox
      {...props}
      name={name}
      error={error}
      forceError={!!error}
      fullWidth={fullWidth}
      margin={margin}
      InputProps={withSystemInputClass(InputProps)}
    />
  </SystemFieldControl>
);

// Built on TextBox directly, not PriceBox — PriceBox always overwrites its own InputProps
// with just the currency adornment (see its source), which would silently drop the
// `studio-input` class this whole file relies on to get the flat System* look.
export const SystemPriceInput = ({ InputProps, label, name, error, fullWidth = true, margin = 'none', ...props }) => (
  <SystemFieldControl label={label} name={name} error={error}>
    <TextBox
      type="number"
      step=".01"
      {...props}
      name={name}
      error={error}
      forceError={!!error}
      fullWidth={fullWidth}
      margin={margin}
      InputProps={withSystemInputClass({ ...InputProps, startAdornment: <CurrencyAdornment /> })}
    />
  </SystemFieldControl>
);

// A plain start/end date (or time) pair with the same label-above look as every other
// System* field. Built directly on DateTimePicker rather than the shared DateRange —
// DateRange hardcodes a truthy label fallback on each picker (so it can't be suppressed
// to make room for an external label) and always renders MUI's own notched/floating
// label. Skipping the `label` prop entirely here means MUI never renders that internal
// label, leaving only the external SystemFieldControl one. This intentionally drops
// DateRange's own cross-field "end before start"/"end before start time" validation
// message — for a plain from/to range, that wasn't worth reimplementing; use the plain
// DateRange for ranges that need that validation.
//
// Pass startDateFieldName/endDateFieldName for a date range, or
// startTimeFieldName/endTimeFieldName for a time-only range (DateTimePicker itself
// already renders a KeyboardTimePicker instead of KeyboardDatePicker whenever only a
// `timeFieldName` is given — see HolidayForm.js, which needs both a date range and a
// separate daily time range on the same form).
//
// `native` (default false, so every existing caller — Holidays, Memberships, Sessions,
// Courses… — keeps the MUI picker unchanged) swaps both fields for a plain
// `<input type="date">`, same recipe as the reports2 filters' `FilterDateField`
// (modules/providers/reports2/components/FilterDateField.js): the MUI popup calendar
// was never skinned with dealTokens (only the input box gets the flat `studio-input`
// look via InputProps), so it renders as a generic Material dialog that visually
// clashes with the flat System* fields around it. The native control sidesteps that
// entirely — no popup to skin, no portal z-index concerns inside a modal — and reuses
// the browser's own keyboard-/locale-friendly date UI. Day-only, so only meaningful
// for date ranges (startDateFieldName/endDateFieldName), not time-only ones.
export const SystemDateRange = ({
  startDateFieldName,
  endDateFieldName,
  startTimeFieldName,
  endTimeFieldName,
  startValue,
  endValue,
  startLabel,
  endLabel,
  onStartChange,
  onEndChange,
  disableStart,
  disableEnd,
  // DateTimePicker defaults disablePast to true, which is right for scheduling
  // something new but wrong for editing a range that may already have started
  // (e.g. an active video/service assignment) — a caller editing an existing
  // range should pass disablePast={false}.
  disablePast = true,
  z = true,
  native = false,
}) => {
  const { t } = useTranslation();

  if (native) {
    const nativeInputStyle = disabled => ({
      width: '100%',
      height: 36,
      boxSizing: 'border-box',
      padding: '7px 11px',
      border: `1px solid ${dealTokens.color.border}`,
      borderRadius: dealTokens.radius.md,
      fontSize: 13.5,
      fontFamily: dealTokens.font.family,
      color: dealTokens.color.text,
      background: disabled ? dealTokens.color.canvas : dealTokens.color.surface,
    });
    return (
      <SystemFormGrid minColumnWidth={160} noMargin>
        <SystemFieldControl label={startLabel || t('GENERAL.from')} name={startDateFieldName}>
          <input
            type="date"
            name={startDateFieldName}
            value={printInputDate(startValue)}
            onChange={event => onStartChange?.(event.target.value)}
            disabled={disableStart}
            style={nativeInputStyle(disableStart)}
          />
        </SystemFieldControl>
        <SystemFieldControl label={endLabel || t('GENERAL.to')} name={endDateFieldName}>
          <input
            type="date"
            name={endDateFieldName}
            value={printInputDate(endValue)}
            onChange={event => onEndChange?.(event.target.value)}
            disabled={disableEnd}
            style={nativeInputStyle(disableEnd)}
          />
        </SystemFieldControl>
      </SystemFormGrid>
    );
  }

  return (
    <SystemFormGrid minColumnWidth={160} noMargin>
      <SystemFieldControl label={startLabel || t('GENERAL.from')} name={startDateFieldName || startTimeFieldName}>
        <DateTimePicker
          dateFieldName={startDateFieldName}
          timeFieldName={startTimeFieldName}
          value={startValue}
          onChange={onStartChange}
          disabled={disableStart}
          disablePast={disablePast}
          margin="none"
          fullWidth
          z={z}
          InputProps={{ className: 'studio-input' }}
        />
      </SystemFieldControl>
      <SystemFieldControl label={endLabel || t('GENERAL.to')} name={endDateFieldName || endTimeFieldName}>
        <DateTimePicker
          dateFieldName={endDateFieldName}
          timeFieldName={endTimeFieldName}
          value={endValue}
          onChange={onEndChange}
          disabled={disableEnd}
          disablePast={disablePast}
          margin="none"
          fullWidth
          z={z}
          InputProps={{ className: 'studio-input' }}
        />
      </SystemFieldControl>
    </SystemFormGrid>
  );
};

export const SystemSearchInput = ({ InputProps, error, ...props }) => (
  <SearchBox {...props} error={error} forceError={!!error} InputProps={withSystemInputClass(InputProps)} />
);

// Autocomplete (react-select, multi-select chips) has its own MUI TextField wrapper deep
// inside a custom react-select `Control` component that hardcodes `InputProps={{
// inputComponent, inputProps }}` for the react-select/MUI integration — passing our own
// InputProps the way the other System* controls do would clobber `inputComponent` and
// break the widget. Styling it from a wrapping element instead sidesteps that entirely:
// `.MuiOutlinedInput-root` is a real class on the DOM node regardless of how Autocomplete's
// props are plumbed, so a plain CSS descendant selector reaches it. Not passing `label`
// (SystemFieldControl renders the external one) keeps this consistent with every other
// System* control that suppresses TextBox's own internal floating label the same way.
//
// The .MuiAutocomplete-values/.MuiInputBase-input rules below are the same fix
// GeneralInformation.js's page-local `MultiSelectField` carries for its secondaryCategoryIds
// field: react-select's own layout puts the clear/dropdown icons in the same flex-wrap row
// as the chips. With several chips selected they wrap onto a 2nd line and, since the icons
// are a sibling flex item with no flex-basis of their own, the whole icon group gets pushed
// below the wrapped chips instead of staying pinned top-right. `flex-basis: 0%` (not `auto`)
// on the chip row is what actually matters: the browser decides which items fit on a
// flex-wrap line using each item's flex-basis BEFORE flex-shrink is applied, so with `auto`
// the chip row alone is already deemed to overflow line 1 and the icons get bumped to line 2
// regardless of flex-shrink. Centralized here (unlike GeneralInformation.js's copy, which is
// deliberately scoped to not change the many pages still using bare Autocomplete) because
// every future SystemAutocomplete consumer should get it for free.
const AutocompleteSkin = styled.div`
  width: 100%;
  .MuiOutlinedInput-root {
    min-height: 36px;
    box-sizing: border-box;
    border-radius: ${dealTokens.radius.md}px;
    background-color: ${dealTokens.color.surface};
    font-size: 13.5px;
    color: ${dealTokens.color.text};
  }
  .MuiOutlinedInput-root fieldset {
    border-color: ${dealTokens.color.border};
  }
  .MuiOutlinedInput-root:hover fieldset {
    border-color: ${dealTokens.color.border};
  }
  .MuiOutlinedInput-root.Mui-focused fieldset {
    border-color: ${C.accent};
    border-width: 1px;
  }
  .MuiOutlinedInput-input {
    padding: 8px 11px;
    align-items: flex-start;
  }
  .MuiAutocomplete-values {
    flex: 1 1 0%;
    min-width: 0;
  }
  .MuiInputBase-input > div:not(.MuiAutocomplete-values) {
    flex-shrink: 0;
  }
  .MuiChip-root {
    margin-bottom: 6px;
  }
`;

// The option list is portalled by default so a card, dialog body or sticky footer can't clip or
// cover it; pass dropdownPortal={false} to render it in place.
export const SystemAutocomplete = ({ label, name, error, className, margin = 'none', dropdownPortal = true, ...props }) => (
  <SystemFieldControl label={label} name={name} error={error}>
    <AutocompleteSkin>
      <Autocomplete
        // Open upwards when there is no room below (e.g. near the bottom of a modal).
        menuPlacement="auto"
        {...props}
        dropdownPortal={dropdownPortal}
        name={name}
        error={!!error}
        helperText={error}
        fullWidth
        // Autocomplete defaults to margin="normal" (MUI's marginNormal override adds margin
        // on every side, meant for label-less legacy forms) — same trap called out for
        // SystemTextInput above; SystemFieldControl already provides the label and
        // SystemFormGrid its own spacing, so left at the default this doubles up against a
        // wrapping grid's margin-bottom instead of just adding it once.
        margin={margin}
        // The base MuiFormControl theme override caps width at a fixed formControlSize —
        // fullWidth alone doesn't undo it for react-select's nested divs. Every existing
        // Autocomplete caller across the app already works around this with this same
        // class (see workoutPlansTemplates/index.css); baked in here so nobody has to
        // remember it for a new SystemAutocomplete usage.
        className={['myWorkoutsAutocomplete', className].filter(Boolean).join(' ')}
      />
    </AutocompleteSkin>
  </SystemFieldControl>
);

const SelectControlSkin = styled.div`
  width: 100%;
  .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline {
    border-color: ${dealTokens.color.borderStrong};
  }
  .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${dealTokens.color.border};
  }
  .MuiOutlinedInput-root.Mui-disabled {
    background: ${dealTokens.color.surface};
    opacity: 0.55;
  }
  /* A long value ends with "…" before the arrow instead of running under it. */
  .MuiSelect-select {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 36px !important;
  }
`;

export const SystemSelect = ({ className, label, name, error, required = false, fullWidth = true, margin = 'none', ...props }) => (
  <SystemFieldControl label={label} name={name} error={error} required={required}>
    <SelectControlSkin>
      <SelectOption
        {...props}
        name={name}
        required={required}
        throwError={!!error}
        errorMessage={error}
        fullWidth={fullWidth}
        margin={margin}
        className={['studio-input', className].filter(Boolean).join(' ')}
      />
    </SelectControlSkin>
  </SystemFieldControl>
);

export const SystemFieldError = ({ children }) =>
  children ? <div style={{ color: '#E53C4C', fontSize: 11.5, lineHeight: 1.35, margin: '6px 0 10px' }}>{children}</div> : null;

export const SystemCheckbox = ({ checked, onChange, label, description, disabled = false, error }) => (
  <div style={{ width: '100%' }}>
    <label
      style={{
        display: 'flex',
        alignItems: description ? 'flex-start' : 'center',
        gap: 9,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <BrandedCheckbox
        checked={!!checked}
        onChange={event => onChange?.(event.target.checked, event)}
        disabled={disabled}
        disableRipple
        icon={<CheckboxMark />}
        checkedIcon={<CheckboxMark data-checked="true" />}
      />
      <span style={{ minWidth: 0, paddingTop: description ? 1 : 0 }}>
        <span style={{ display: 'block', color: error ? '#E53C4C' : C.ink, fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>{label}</span>
        {description && <span style={{ display: 'block', color: C.muted, fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>{description}</span>}
      </span>
    </label>
    <SystemFieldError>{error}</SystemFieldError>
  </div>
);

export const SystemFormGrid = ({ children, minColumnWidth, columnGap, rowGap, noMargin = false, singleColumn = false, ...props }) => (
  <FormGrid
    minColumnWidth={minColumnWidth}
    columnGap={columnGap}
    rowGap={rowGap}
    noMargin={noMargin}
    data-single-column={singleColumn ? 'true' : undefined}
    {...props}
  >
    {children}
  </FormGrid>
);

export const SystemFormSection = ({ title, description, action, children, accent = false, noMargin = false, variant = 'card', style }) => (
  <FormSection data-variant={variant} data-accent={accent ? 'true' : undefined} data-no-margin={noMargin ? 'true' : undefined} style={style}>
    {(title || description || action) && (
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          {title && <div style={{ color: C.ink, fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>{title}</div>}
          {description && <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>{description}</div>}
        </div>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </div>
    )}
    {children}
  </FormSection>
);

// Collapsible block inside a form section: a full-width header row (label, optional muted
// summary, chevron) that shows/hides its children. Controlled (`open` + `onToggle`) so the
// form decides the default — e.g. the Exercise builder keeps optional long fields closed
// to bring Media above the fold. Children stay unmounted while closed.
export const SystemDisclosure = ({ label, summary, open, onToggle, children }) => (
  <div>
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: dealTokens.space[2],
        width: '100%',
        padding: 0,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
      }}
    >
      <span style={{ color: C.ink, fontSize: 13, fontWeight: 500 }}>{label}</span>
      {summary && (
        <span style={{ color: C.muted, fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {summary}
        </span>
      )}
      {open ? (
        <ExpandLessIcon style={{ marginLeft: 'auto', fontSize: 20, color: C.muted }} />
      ) : (
        <ExpandMoreIcon style={{ marginLeft: 'auto', fontSize: 20, color: C.muted }} />
      )}
    </button>
    {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 16 }}>{children}</div>}
  </div>
);

// WizardSlider renders its own label via the legacy `SmallGrayText` (different font
// size/weight/color than SystemFieldControl's label) and defaults to an asymmetric
// `16px 16px 8px 16px` margin — both stood out visually next to sibling System*
// fields, which all render a flush-left, uniform label and rely on the parent's flex
// `gap` for spacing instead of their own margin. The label has a hardcoded
// `id="input-slider"` (pre-existing in WizardSlider, not introduced here) rather
// than one derived from `name`, so this targets it by id — fine here since every
// slider on a page should look identical anyway.
const SliderLabel = styled.div`
  #input-slider {
    display: block;
    margin-bottom: 6px;
    color: ${C.muted};
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
  }
`;

// Thin wrapper over the existing shared WizardSlider (same trick every other System*
// control uses: reuse the underlying widget verbatim, restyle only) — WizardSlider's
// MUI Slider/Input read their color from the ambient MUI theme's `palette.primary`,
// which on Deal is black (`dealThemePalette.primary`), not the dealTokens coral. A
// scoped ThemeProvider overriding just `primary.main` fixes the color without
// touching WizardSlider.js itself or the app-wide theme. `margin` defaults to the
// string `'0'` (same reasoning as SystemTextInput/SystemSelect defaulting
// `margin="none"`) — it has to be a non-empty string, not the number `0`:
// WizardSlider does `margin: margin || '16px 16px 8px 16px'`, and `0` is falsy in
// JS, so a numeric `0` silently loses to the fallback and the 16px horizontal inset
// stays, which is exactly what caused this slider to sit indented relative to the
// flush-edged fields around it even after the label-only fix above. A caller can
// still override this explicitly.
export const SystemSlider = ({ margin = '0', ...props }) => {
  const outerTheme = useTheme();
  const theme = createTheme({ ...outerTheme, palette: { ...outerTheme.palette, primary: { main: dealTokens.color.primary } } });
  return (
    <ThemeProvider theme={theme}>
      <SliderLabel>
        <WizardSlider margin={margin} {...props} />
      </SliderLabel>
    </ThemeProvider>
  );
};

// Thin wrapper over MUI's Radio/RadioGroup/FormControlLabel (no bespoke equivalent
// exists in @guryou/html-components to wrap instead, unlike every other System*
// control) — styled to match the rest of the System* controls (SystemFieldControl
// label, coral-checked radio) and normalized to the same value-first onChange
// contract SystemSelect/SystemCheckbox already use (`onChange(value)`, not a raw DOM
// event), instead of RadioGroup's native `onChange(event)`.
export const SystemRadioGroup = ({ label, name, value, onChange, options = [], row = true, error }) => (
  <SystemFieldControl label={label} name={name} error={error}>
    <RadioGroup row={row} name={name} value={value} onChange={e => onChange?.(e.target.value)}>
      {options.map(option => (
        <FormControlLabel key={option.value} value={option.value} control={<BrandedRadio />} label={option.label} />
      ))}
    </RadioGroup>
  </SystemFieldControl>
);

// Segmented control for a small, mutually-exclusive choice (2-4 options) — a single
// canvas track with a floating coral pill on the active segment, instead of a row of
// separate outline/ghost buttons that reads as independent actions rather than one
// control. `fullWidth` (default true) splits the track evenly across its container,
// each segment `flex: 1`; pass `false` for a control that sizes to its own content
// instead (e.g. centered above a form, not spanning a whole card) — the caller
// centers it by wrapping in its own `justifyContent: 'center'` flex row, same as
// every other centered row in this codebase, rather than this component guessing at
// its container's alignment. No resting border on the inactive segments on purpose:
// with the active side always preselected by the caller, a bare hover fill is enough
// once there's a permanently-visible coral pill to establish "this is a toggle".
// An option with `disabled: true` renders dimmed and can't be picked (e.g. a mode the
// backend doesn't support yet); its optional `hint` shows as the native tooltip.
export const SystemSegmentedControl = ({ value, onChange, options, fullWidth = true }) => (
  <div
    style={{
      display: 'flex',
      width: fullWidth ? '100%' : 'fit-content',
      gap: 2,
      padding: 3,
      background: dealTokens.color.canvas,
      border: `1px solid ${dealTokens.color.border}`,
      borderRadius: dealTokens.radius.lg,
    }}
  >
    {options.map(option => {
      const active = value === option.value;
      return (
        <button
          key={String(option.value)}
          type="button"
          disabled={option.disabled}
          aria-pressed={active}
          title={option.hint}
          onClick={() => onChange(option.value)}
          onMouseEnter={e => {
            if (!active && !option.disabled) e.currentTarget.style.background = dealTokens.color.surface;
          }}
          onMouseLeave={e => {
            if (!active) e.currentTarget.style.background = 'transparent';
          }}
          style={{
            flex: fullWidth ? 1 : 'initial',
            padding: '9px 18px',
            border: 'none',
            borderRadius: dealTokens.radius.md,
            background: active ? C.accent : 'transparent',
            color: active ? '#fff' : C.muted,
            fontFamily: dealTokens.font.family,
            fontSize: 13,
            fontWeight: dealTokens.font.weightMedium,
            boxShadow: active ? dealTokens.shadow.subtle : 'none',
            cursor: option.disabled ? 'not-allowed' : 'pointer',
            opacity: option.disabled ? 0.5 : 1,
            transition: `background ${dealTokens.motion.fast}, color ${dealTokens.motion.fast}, box-shadow ${dealTokens.motion.fast}`,
          }}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);
